import { useState, useEffect, useMemo, useRef } from "react";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import RevealRenderer from "@/features/tutor/components/RevealRenderer";
import { debugLog } from "@/utils/debugLog";
import { sanitizeSlideHtml } from "@/features/tutor/vo-demo/utils/sanitizeSlideHtml";
import {
  hoistSlideStylesheets,
  removeHoistedSlideStyles,
} from "@/features/tutor/vo-demo/utils/hoistSlideStylesheets";
import { useAuthStore } from "@/store/useAuthStore";
import { useDebugStore } from "@/store/useDebugStore";
import {
  getLessonStartupPerfNow,
  recordLessonStartupPerf,
  resolveLessonStartupPerfVariant,
} from "@/features/tutor/utils/lessonStartupPerf";

/** Slide HTML together with the slide it was loaded for. */
type LoadedSlide = {
  html: string;
  lessonId: unknown;
  slideIndex: number | null;
};

const NO_SLIDE_LOADED: LoadedSlide = {
  html: "",
  lessonId: null,
  slideIndex: null,
};

const Slide = () => {
  const [loadedSlide, setLoadedSlide] = useState<LoadedSlide>(NO_SLIDE_LOADED);
  const {
    slides,
    currentSlideIndex,
    getSlideByIndex,
    currentUrl,
    currentLessonId,
    loadSlideHtml,
    slideResetVersion,
  } = useSlideStore();
  const userStartupPerfVariant = useAuthStore(
    (state) => state.user?.config?.lessonStartupPerfVariant,
  );
  const lessonStartupPerfOverride = useDebugStore(
    (state) => state.lessonStartupPerfOverride,
  );
  const lessonStartupPerfVariant = useMemo(
    () =>
      resolveLessonStartupPerfVariant(
        userStartupPerfVariant,
        lessonStartupPerfOverride,
      ),
    [lessonStartupPerfOverride, userStartupPerfVariant],
  );
  const useSlideHtmlCache = lessonStartupPerfVariant === "optimized";
  const slideRequestIdRef = useRef(0);
  const firstSlideContentRecordedRef = useRef(false);
  // The lesson whose slide styles are in <head>.
  const stylesLessonIdRef = useRef<unknown>(null);

  useEffect(() => {
    firstSlideContentRecordedRef.current = false;
  }, [currentLessonId]);

  useEffect(() => {
    let directFetchAbortController: AbortController | null = null;

    // The previous lesson's styles go as its slide leaves the screen, which is
    // here and not on a lesson id change: SlideEditor keeps this component
    // across lessons, and the old slide stays up until the new one arrives.
    // ORDER MATTERS: remove before hoisting, as both lessons share the text.
    const showSlideHtml = (html: string, shouldHoistStyles = false) => {
      if (stylesLessonIdRef.current !== currentLessonId) {
        removeHoistedSlideStyles();
        stylesLessonIdRef.current = currentLessonId;
      }
      setLoadedSlide({
        html: shouldHoistStyles ? hoistSlideStylesheets(html) : html,
        lessonId: currentLessonId,
        slideIndex: currentSlideIndex,
      });
    };

    const fetchSlideContent = async () => {
      const requestId = ++slideRequestIdRef.current;
      debugLog("Fetching slide content for slide index:", currentSlideIndex);
      debugLog("Slides:", slides);
      if (
        Array.isArray(slides) &&
        slides.length > 0 &&
        currentSlideIndex !== undefined &&
        currentSlideIndex !== null
      ) {
        const currentSlide = getSlideByIndex(currentSlideIndex);
        if (!currentSlide?.slide_html) {
          if (requestId === slideRequestIdRef.current) {
            showSlideHtml("");
          }
          return;
        }

        if (!useSlideHtmlCache) {
          directFetchAbortController = new AbortController();
        }

        try {
          const slideLoadStartedAt = getLessonStartupPerfNow();
          const htmlContent = await loadSlideHtml({
            lessonId: currentLessonId,
            slideIndex: currentSlideIndex,
            sourceUrl: currentSlide.slide_html,
            variant: lessonStartupPerfVariant,
            cacheEnabled: useSlideHtmlCache,
            signal: directFetchAbortController?.signal,
          });
          if (requestId !== slideRequestIdRef.current) {
            return;
          }
          showSlideHtml(sanitizeSlideHtml(htmlContent), true);
          if (!firstSlideContentRecordedRef.current) {
            firstSlideContentRecordedRef.current = true;
            recordLessonStartupPerf(
              "first_slide_content_ready",
              {
                lesson_id: currentLessonId,
                slide_index: currentSlideIndex,
                variant: lessonStartupPerfVariant,
                cache_enabled: useSlideHtmlCache,
              },
              slideLoadStartedAt,
            );
          }
        } catch (error) {
          if (requestId !== slideRequestIdRef.current) {
            return;
          }
          if (error instanceof DOMException && error.name === "AbortError") {
            return;
          }
          console.error("Failed to load slide content:", error);
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          showSlideHtml(
            `<div class="error">Failed to load slide content: ${errorMessage}</div>`,
          );
        }
      } else {
        if (requestId === slideRequestIdRef.current) {
          showSlideHtml("");
        }
      }
    };
    fetchSlideContent();

    return () => {
      directFetchAbortController?.abort();
    };
  }, [
    currentLessonId,
    slides,
    currentSlideIndex,
    getSlideByIndex,
    currentUrl,
    loadSlideHtml,
    lessonStartupPerfVariant,
    useSlideHtmlCache,
  ]);

  if (!currentUrl) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50 border-l border-gray-200">
        <div className="text-center">
          <div className="text-gray-500 text-lg">No slide content</div>
        </div>
      </div>
    );
  }

  // Keyed by the slide the HTML belongs to, not by currentSlideIndex: the index
  // moves first and the HTML arrives after, and a key on the index remounted
  // Reveal on the outgoing slide's HTML and then again on the new one's.
  const isLoadedSlideCurrent =
    loadedSlide.lessonId === currentLessonId &&
    loadedSlide.slideIndex === currentSlideIndex;
  return (
    <div className="h-full w-full">
      <RevealRenderer
        key={`${loadedSlide.lessonId}-${loadedSlide.slideIndex}-${slideResetVersion}`}
        slideContent={loadedSlide.html}
        slideIndex={loadedSlide.slideIndex}
        isCurrent={isLoadedSlideCurrent}
      />
    </div>
  );
};

export default Slide;
